<script setup>
import { computed, ref, reactive, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['deleteSuccess']);

const store = useStore();

// Modal for editing instruction
const instructionModalRef = ref(null);
const tempInstruction = ref('');

const isSyncing = ref(false);

// Provider definitions with their models
const providers = {
  openai: {
    label: 'OpenAI',
    placeholder: 'sk-...',
    models: [
      { value: 'gpt-4o', label: 'GPT-4o (Recomendado)' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
      { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    ],
  },
  anthropic: {
    label: 'Anthropic (Claude)',
    placeholder: 'sk-ant-...',
    models: [
      {
        value: 'claude-sonnet-4-20250514',
        label: 'Claude Sonnet 4 (Recomendado)',
      },
      { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
      { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
    ],
  },
  google: {
    label: 'Google (Gemini)',
    placeholder: 'AIza...',
    models: [
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Recomendado)' },
      { value: 'gemini-2.5-pro-preview-06-05', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-2.5-flash-preview-05-20', label: 'Gemini 2.5 Flash' },
    ],
  },
  groq: {
    label: 'Groq',
    placeholder: 'gsk_...',
    models: [
      {
        value: 'llama-3.3-70b-versatile',
        label: 'Llama 3.3 70B (Recomendado)',
      },
      { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B' },
      { value: 'mixtral-8x7b-32768', label: 'Mixtral 8x7B' },
    ],
  },
};

// State must be defined before computed properties that use it
const state = reactive({
  // Basic info
  name: '',
  description: '',
  // IA specific
  role: '',
  goal: '',
  instruction: '',
  // LLM config
  provider: 'openai',
  model: 'gpt-4o',
  temperature: 0.7,
  apiKey: '',
  // Sync status
  externalAgentId: '',
});

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

// Provider-related computed
const providerOptions = computed(() =>
  Object.entries(providers).map(([key, prov]) => ({
    value: key,
    label: prov.label,
  }))
);
const currentProvider = computed(
  () => providers[state.provider] || providers.openai
);
const availableModels = computed(() => currentProvider.value.models);
const hasApiKey = computed(() => !!state.apiKey);

// Validation
const nameError = computed(() => {
  if (!state.name) return 'Nome e obrigatorio';
  if (/\s/.test(state.name)) return 'Nome nao pode conter espacos';
  return '';
});

// Reset model when provider changes
watch(
  () => state.provider,
  newProvider => {
    const providerModels = providers[newProvider]?.models || [];
    state.model = providerModels[0]?.value || '';
  }
);

const isSynced = computed(() => !!state.externalAgentId);

const updateStateFromAgent = agentData => {
  if (!agentData) return;

  const config = agentData.config || {};

  // Basic info
  state.name = agentData.name || '';
  state.description = agentData.description || '';

  // IA specific fields
  state.role = agentData.role || '';
  state.goal = agentData.goal || '';
  state.instruction = agentData.instruction || '';
  state.temperature = agentData.temperature ?? 0.7;

  // LLM config
  state.provider = agentData.provider || config.provider || 'openai';
  state.model = agentData.model || 'gpt-4o';
  state.apiKey = config.llm_api_key || '';

  // Sync status
  state.externalAgentId = agentData.external_agent_id || '';
};

// Watch agent changes
watch(
  agent,
  newAgent => {
    if (newAgent) updateStateFromAgent(newAgent);
  },
  { immediate: true, deep: false }
);

const handleSubmit = async () => {
  if (nameError.value) {
    useAlert(nameError.value);
    return;
  }

  try {
    const payload = {
      id: props.agentId,
      name: state.name,
      description: state.description,
      agent_type: 'llm',
      role: state.role,
      goal: state.goal,
      instruction: state.instruction,
      provider: state.provider,
      model: state.model,
      temperature: parseFloat(state.temperature),
      config: {
        provider: state.provider,
        ...(state.apiKey ? { llm_api_key: state.apiKey } : {}),
      },
    };

    await store.dispatch('aifocusAgents/update', payload);
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('Agente salvo e sincronizado');
  } catch (error) {
    const errorMessage = error?.message || 'Erro ao atualizar agente';
    useAlert(errorMessage);
  }
};

const handleSync = async () => {
  try {
    isSyncing.value = true;
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    useAlert('Agente sincronizado com IA');
    await store.dispatch('aifocusAgents/show', props.agentId);
  } catch (error) {
    const errorMessage = error?.message || 'Erro ao sincronizar com IA';
    useAlert(errorMessage);
  } finally {
    isSyncing.value = false;
  }
};

const handleDelete = () => {
  emit('deleteSuccess');
};

// Instruction Modal functions
const openInstructionModal = () => {
  tempInstruction.value = state.instruction;
  instructionModalRef.value?.open();
};

const saveInstruction = () => {
  state.instruction = tempInstruction.value;
  instructionModalRef.value?.close();
};

const cancelInstructionEdit = () => {
  tempInstruction.value = state.instruction;
};

defineExpose({
  state,
  isSynced,
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 pb-8">
    <!-- Basic Information -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        heading="Informacoes do Agente"
        description="Configure as informacoes principais do agente"
      />

      <Input
        v-model="state.name"
        label="Nome do Agente"
        placeholder="nome_do_agente (sem espacos)"
        :message="nameError || 'Use apenas letras, numeros, _ e -'"
        :message-type="nameError ? 'error' : 'info'"
      />

      <Input
        v-model="state.description"
        label="Descricao do Agente"
        placeholder="Descreva o que este agente faz..."
      />

      <Input
        v-model="state.role"
        label="Role (Papel)"
        placeholder="Ex: Consultor Virtual de Vendas"
        message="Define quem o agente e e como ele se apresenta"
      />

      <Input
        v-model="state.goal"
        label="Goal (Objetivo)"
        placeholder="Ex: Apresentar solucoes, qualificar leads, agendar reunioes"
        message="Define os objetivos principais do agente"
      />

      <span class="h-px w-full bg-n-weak mt-2" />

      <SettingsHeader
        heading="Modelo de IA"
        description="Selecione o provedor e modelo para o agente"
      />

      <!-- Provider Selection -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">Provedor</label>
        <ComboBox
          v-model="state.provider"
          :options="providerOptions"
          placeholder="Selecione um provedor..."
          search-placeholder="Buscar provedor..."
        />
      </div>

      <!-- Model Selection -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">Modelo</label>
        <ComboBox
          v-model="state.model"
          :options="availableModels"
          placeholder="Selecione um modelo..."
          search-placeholder="Buscar modelo..."
        />
      </div>

      <!-- API Key -->
      <div class="flex flex-col gap-2">
        <Input
          v-model="state.apiKey"
          type="password"
          :label="`Chave da API (${currentProvider.label})`"
          :placeholder="currentProvider.placeholder"
        />
        <div class="flex items-center gap-2">
          <span
            class="w-2 h-2 rounded-full"
            :class="hasApiKey ? 'bg-green-500' : 'bg-red-500'"
          />
          <p class="text-xs text-n-slate-11">
            {{
              hasApiKey
                ? 'Chave configurada'
                : 'Insira sua chave de API para o agente funcionar'
            }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Temperature: {{ state.temperature }}
        </label>
        <div class="flex items-center gap-3">
          <input
            v-model="state.temperature"
            type="range"
            min="0"
            max="1"
            step="0.1"
            class="flex-1"
          />
          <Input
            v-model="state.temperature"
            type="number"
            size="sm"
            custom-input-class="w-16 text-center"
            min="0"
            max="1"
          />
        </div>
        <p class="text-sm text-n-slate-11 italic">
          Quanto maior, mais criativo. Quanto menor, mais preciso.
        </p>
      </div>
    </div>

    <!-- Instructions and Messages -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        heading="Instruction (System Prompt)"
        description="Defina como o agente deve se comportar"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Instrucoes do Agente
        </label>
        <div
          class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 cursor-pointer hover:bg-n-alpha-2 transition-colors min-h-[150px] max-h-[200px] overflow-hidden relative group"
          @click="openInstructionModal"
        >
          <div class="text-sm whitespace-pre-wrap line-clamp-6">
            {{
              state.instruction ||
              'Clique para adicionar instrucoes completas do agente...'
            }}
          </div>
          <div
            v-if="state.instruction"
            class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-n-alpha-1 to-transparent"
          />
          <div
            class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <span class="i-lucide-expand text-n-slate-11" />
          </div>
        </div>
        <p class="text-xs text-n-slate-11">
          Clique para expandir e editar o prompt completo. Aqui voce define
          todas as instrucoes, regras e comportamentos do agente.
        </p>
      </div>

      <span class="h-px w-full bg-n-weak mt-2" />

      <!-- Messages info - redirect to Atendimento tab -->
      <div class="p-4 bg-n-alpha-1 border border-n-weak rounded-xl">
        <div class="flex items-center gap-3">
          <span class="i-lucide-message-square text-n-slate-11 text-xl" />
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              Mensagens Automaticas
            </p>
            <p class="text-xs text-n-slate-10">
              As mensagens de boas-vindas e transferencia sao configuradas na
              aba Atendimento.
            </p>
          </div>
        </div>
      </div>

      <span class="h-px w-full bg-n-weak mt-2" />

      <!-- Sync Status -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Status de Sincronizacao
        </label>
        <div class="flex items-center gap-2">
          <span
            class="w-3 h-3 rounded-full"
            :class="isSynced ? 'bg-green-500' : 'bg-yellow-500'"
          />
          <span class="text-sm text-n-slate-11">
            {{ isSynced ? 'Sincronizado com Agno' : 'Nao sincronizado' }}
          </span>
        </div>
        <p
          v-if="state.externalAgentId"
          class="text-xs text-n-slate-10 font-mono"
        >
          ID: {{ state.externalAgentId }}
        </p>
      </div>

      <div class="flex gap-3">
        <Button
          label="Salvar Alteracoes"
          :is-loading="isUpdating"
          @click="handleSubmit"
        />
        <Button
          label="Sincronizar"
          variant="secondary"
          :is-loading="isSyncing"
          @click="handleSync"
        />
      </div>

      <span class="h-px w-full bg-n-weak mt-4" />

      <!-- Delete Section -->
      <div class="flex items-end justify-between w-full gap-4">
        <div class="flex flex-col gap-2">
          <h6 class="text-n-slate-12 text-base font-medium">Excluir Agente</h6>
          <span class="text-n-slate-11 text-sm">
            Esta acao e irreversivel e excluira o agente permanentemente.
          </span>
        </div>
        <div class="flex-shrink-0">
          <Button
            :label="`Excluir ${state.name || 'Agente'}`"
            color="ruby"
            class="max-w-56 !w-fit"
            @click="handleDelete"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Instruction Modal -->
  <Dialog
    ref="instructionModalRef"
    title="Instruction (System Prompt)"
    description="Configure o prompt do sistema para seu agente. Este e o contexto inicial que define como o agente deve se comportar, suas regras, restricoes e personalidade."
    confirm-button-label="Salvar"
    cancel-button-label="Cancelar"
    width="3xl"
    overflow-y-auto
    @confirm="saveInstruction"
    @close="cancelInstructionEdit"
  >
    <div class="flex flex-col gap-4">
      <div class="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p class="text-sm text-amber-800">
          <strong>Dica:</strong> Inclua instrucoes claras sobre o que o agente
          pode e deve fazer, como responder a diferentes situacoes, e quais
          informacoes ele deve ou nao compartilhar.
        </p>
      </div>
      <TextArea
        v-model="tempInstruction"
        placeholder="Voce e um assistente virtual especializado em..."
        resize
        show-character-count
        :max-length="50000"
        min-height="400px"
        max-height="600px"
        custom-text-area-class="font-mono text-sm leading-relaxed"
      />
    </div>
  </Dialog>
</template>
